import { Sun, Moon } from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";

/**
 * Compact 2-position slider that toggles between light and dark themes.
 * Rendered in the site header for ANONYMOUS users (logged-in users use the
 * dropdown Appearance submenu instead).
 *
 * A11y: it's a real <button role="switch"> with aria-checked reflecting the
 * current resolved mode. Keyboard: Enter / Space toggle. Screen readers read
 * "Toggle theme, currently light/dark".
 */
export default function ThemeToggle() {
  const { resolvedMode, setMode } = useTheme();
  const isDark = resolvedMode === "dark";

  const toggle = () => setMode(isDark ? "light" : "dark");

  return (
    <button
      type="button"
      role="switch"
      aria-checked={isDark}
      aria-label={`Toggle theme, currently ${isDark ? "dark" : "light"}`}
      data-testid="nav-theme-toggle"
      onClick={toggle}
      className={`relative inline-flex items-center h-8 w-14 rounded-full border transition-colors focus:outline-none focus:ring-2 focus:ring-sky-600 focus:ring-offset-1 dark:focus:ring-offset-slate-900 ${
        isDark
          ? "bg-slate-800 border-slate-700"
          : "bg-slate-100 border-slate-200"
      }`}
    >
      {/* Track icons */}
      <Sun
        className={`absolute left-1.5 w-3.5 h-3.5 transition-opacity ${
          isDark ? "opacity-40 text-slate-500" : "opacity-100 text-amber-500"
        }`}
        aria-hidden
      />
      <Moon
        className={`absolute right-1.5 w-3.5 h-3.5 transition-opacity ${
          isDark ? "opacity-100 text-sky-300" : "opacity-40 text-slate-400"
        }`}
        aria-hidden
      />
      {/* Knob */}
      <span
        aria-hidden
        className={`inline-block w-6 h-6 rounded-full bg-white dark:bg-slate-100 shadow-md ring-1 ring-black/5 transform transition-transform ${
          isDark ? "translate-x-7" : "translate-x-1"
        }`}
      />
    </button>
  );
}
